import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, Tooltip, alpha, useTheme } from '@mui/material';

import type { LensPublication } from '~/types/leLens';

type LensStatusProps = {
  publication: LensPublication;
  ownsDraft: boolean;
  pending: boolean;
  onPublish: () => void;
};

export default function LensStatus({ publication, ownsDraft, pending, onPublish }: LensStatusProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const locked = Boolean(publication.editorEmail && !ownsDraft);
  const changed = publication.publishedVersion === null || publication.hasUnpublishedChanges;
  if (!changed && !locked) return null;

  const fill = theme.palette.mode === 'dark' ? tokens.accent : alpha(tokens.accent, 0.82);
  return (
    <Tooltip
      describeChild
      title={locked ? 'Someone else is editing this draft. Use the menu to take over editing.' : ''}
    >
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Button
          disabled={pending || locked}
          onClick={onPublish}
          size="small"
          startIcon={locked ? <LockOutlinedIcon fontSize="small" /> : undefined}
          sx={{
            bgcolor: fill,
            border: 1,
            borderColor: 'transparent',
            color: tokens.accentContrast,
            fontSize: 14,
            height: 32,
            lineHeight: 1,
            minHeight: 32,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: alpha(tokens.accent, 0.9) },
            '& .MuiButton-startIcon > *': { fontSize: 16 },
            '&.Mui-disabled': locked
              ? { bgcolor: 'action.disabledBackground', borderColor: 'divider', color: 'text.secondary' }
              : { bgcolor: fill, color: tokens.accentContrast, opacity: 0.4 },
          }}
          variant="contained"
        >
          Publish changes
        </Button>
      </Box>
    </Tooltip>
  );
}
